import { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store';
import { addNotification } from '../../../store/uiSlice';
import apiService from '../../../services/api';
import {
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
  CollisionDetection,
  pointerWithin,
  closestCenter,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { OrderItem, CategoryKey } from './types';

interface UseDragReorderParams {
  writingGenres: OrderItem[];
  writingAuthors: OrderItem[];
  illustrationStyles: OrderItem[];
  illustrationArtists: OrderItem[];
}

export function useDragReorder({
  writingGenres,
  writingAuthors,
  illustrationStyles,
  illustrationArtists,
}: UseDragReorderParams) {
  const dispatch = useDispatch<AppDispatch>();

  const [pendingOrder, setPendingOrder] = useState<Record<string, OrderItem[]>>({});
  const [hasUnsavedOrder, setHasUnsavedOrder] = useState(false);
  const [unsavedCategories, setUnsavedCategories] = useState<Set<string>>(new Set());
  const [savingOrder, setSavingOrder] = useState(false);
  const [activeDragId, setActiveDragId] = useState<number | null>(null);
  const [activeDragName, setActiveDragName] = useState<string | null>(null);
  const lastOverIdRef = useRef<string | number | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const customCollisionDetection: CollisionDetection = (args) => {
    const gridContainers = args.droppableContainers.filter(c =>
      String(c.id).startsWith('grid-slot-')
    );
    const cardContainers = args.droppableContainers.filter(c =>
      !String(c.id).startsWith('grid-slot-')
    );

    // 1. pointerWithin — most precise when pointerCoordinates is available
    const pwCollisions = pointerWithin(args);
    if (pwCollisions.length > 0) {
      const gridHits = pwCollisions.filter(c => String(c.id).startsWith('grid-slot-'));
      if (gridHits.length > 0) return gridHits;   // Pointer directly over a grid slot
      return pwCollisions;                          // Pointer over a card — normal sort
    }

    // 2. pointerWithin found nothing (pointerCoordinates null or pointer in a gap)
    //    Use rectIntersection for grid slots — works via collisionRect, no pointer needed
    if (gridContainers.length > 0) {
      const gridRI = rectIntersection({ ...args, droppableContainers: gridContainers });
      if (gridRI.length > 0) return gridRI;
    }

    // 3. Fallback: closestCenter on card containers for left-panel gap sorting
    if (cardContainers.length > 0) {
      return closestCenter({ ...args, droppableContainers: cardContainers });
    }
    return closestCenter(args);
  };

  const initPendingOrder = (category: string, items: OrderItem[]) => {
    const mapped = items.map(i => ({ id: i.id, name: i.name, isActive: i.isActive }));
    // Sort: active items first (maintain relative order), then inactive items.
    // This ensures the top-4 active items visually occupy grid positions 1-4
    // and the divider falls naturally between grid and non-grid items.
    const active = mapped.filter(i => i.isActive);
    const inactive = mapped.filter(i => !i.isActive);
    const sorted = [...active, ...inactive];

    // If re-sorting changed the order, auto-save to persist new sort_order to DB
    const orderChanged = sorted.some((item, idx) => item.id !== mapped[idx].id);

    setPendingOrder(prev => ({
      ...prev,
      [category]: sorted,
    }));

    if (orderChanged) {
      autoSave(category, sorted);
    }
  };

  // Initialize pending order when data loads
  useEffect(() => {
    if (writingGenres.length > 0) initPendingOrder('writing-genres', writingGenres);
  }, [writingGenres]);
  useEffect(() => {
    if (writingAuthors.length > 0) initPendingOrder('writing-authors', writingAuthors);
  }, [writingAuthors]);
  useEffect(() => {
    if (illustrationStyles.length > 0) initPendingOrder('illustration-art-styles', illustrationStyles);
  }, [illustrationStyles]);
  useEffect(() => {
    if (illustrationArtists.length > 0) initPendingOrder('illustration-artists', illustrationArtists);
  }, [illustrationArtists]);

  const getOrderedItems = (category: string, fallback: OrderItem[]): OrderItem[] => {
    return pendingOrder[category] || fallback;
  };

  const handleDragStart = (event: DragStartEvent, currentItems: OrderItem[]) => {
    lastOverIdRef.current = null;
    const activeId = String(event.active.id);

    if (activeId.startsWith('grid-drag-')) {
      const slotIndex = parseInt(activeId.replace('grid-drag-', ''), 10);
      const activeItems = currentItems.filter(i => i.isActive);
      const item = activeItems[slotIndex];
      setActiveDragId(item?.id ?? null);
      setActiveDragName(item?.name || null);
      return;
    }

    const draggedId = event.active.id as number;
    setActiveDragId(draggedId);
    const item = currentItems.find(i => i.id === draggedId);
    setActiveDragName(item?.name || null);
  };

  const handleDragOver = (event: DragOverEvent) => {
    if (event.over) lastOverIdRef.current = event.over.id;
  };

  const handleDragCancel = () => {
    setActiveDragId(null);
    setActiveDragName(null);
    lastOverIdRef.current = null;
  };

  const autoSave = useCallback((category: string, items: OrderItem[]) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        const payload = items.map((item, index) => ({ id: item.id, sort_order: index + 1 }));
        await apiService.reorderAIStyles(category, payload);
        // Silent success — no toast to reduce noise
        setUnsavedCategories(prev => {
          const next = new Set(prev);
          next.delete(category);
          return next;
        });
        setHasUnsavedOrder(prev => {
          // Check if any categories remain unsaved
          const remaining = new Set(unsavedCategories);
          remaining.delete(category);
          return remaining.size > 0;
        });
      } catch {
        // Auto-save failed — show UnsavedChangesBar as fallback
        setHasUnsavedOrder(true);
        setUnsavedCategories(prev => new Set(prev).add(category));
      }
    }, 500);
  }, [unsavedCategories]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  const handleDragEnd = (event: DragEndEvent, category: string, currentItems: OrderItem[]) => {
    setActiveDragId(null);
    setActiveDragName(null);

    const { active, over, collisions } = event;

    let effectiveOverId: string | number | null = over?.id ?? null;
    if (!effectiveOverId && collisions && collisions.length > 0) {
      effectiveOverId = collisions[0].id;
    }
    if (!effectiveOverId) {
      effectiveOverId = lastOverIdRef.current;
    }

    // If collision returned a non-grid target but user was last hovering a grid slot,
    // trust the grid slot (it was visually highlighted — that's what the user intended)
    const lastWasGrid = lastOverIdRef.current != null
      && String(lastOverIdRef.current).startsWith('grid-slot-');
    const currentIsGrid = effectiveOverId != null
      && String(effectiveOverId).startsWith('grid-slot-');
    if (lastWasGrid && !currentIsGrid) {
      effectiveOverId = lastOverIdRef.current;
    }

    lastOverIdRef.current = null;
    if (effectiveOverId == null) return;

    const items = pendingOrder[category] || currentItems.map(i => ({ id: i.id, name: i.name, isActive: i.isActive }));
    const activeItems = items.filter(i => i.isActive);

    // Resolve source index
    const activeIdStr = String(active.id);
    let sourceIndex: number;
    if (activeIdStr.startsWith('grid-drag-')) {
      const slotIndex = parseInt(activeIdStr.replace('grid-drag-', ''), 10);
      const sourceItem = activeItems[slotIndex];
      if (!sourceItem) return;
      sourceIndex = items.findIndex(i => i.id === sourceItem.id);
    } else {
      sourceIndex = items.findIndex(i => i.id === Number(active.id));
    }

    // Resolve target index
    const overIdStr = String(effectiveOverId);
    let targetIndex: number;
    if (overIdStr.startsWith('grid-slot-')) {
      const slotIndex = parseInt(overIdStr.replace('grid-slot-', ''), 10);
      const targetItem = activeItems[slotIndex];
      if (!targetItem) {
        // Empty slot — move to that position
        targetIndex = Math.min(slotIndex, items.length - 1);
      } else {
        targetIndex = items.findIndex(i => i.id === targetItem.id);
      }
    } else if (overIdStr.startsWith('grid-drag-')) {
      const slotIndex = parseInt(overIdStr.replace('grid-drag-', ''), 10);
      const targetItem = activeItems[slotIndex];
      if (!targetItem) return;
      targetIndex = items.findIndex(i => i.id === targetItem.id);
    } else if (overIdStr.startsWith('grid-')) {
      const slotIndex = parseInt(overIdStr.replace('grid-', ''), 10);
      const targetItem = activeItems[slotIndex];
      if (!targetItem) return;
      targetIndex = items.findIndex(i => i.id === targetItem.id);
    } else {
      targetIndex = items.findIndex(i => i.id === Number(effectiveOverId));
    }

    if (sourceIndex === -1 || targetIndex === -1 || sourceIndex === targetIndex) return;

    const reordered = arrayMove(items, sourceIndex, targetIndex);
    setPendingOrder(prev => ({ ...prev, [category]: reordered }));
    autoSave(category, reordered);
  };

  const handleSaveOrder = async (category: string) => {
    const items = pendingOrder[category];
    if (!items) return;

    setSavingOrder(true);
    try {
      const payload = items.map((item, index) => ({ id: item.id, sort_order: index + 1 }));
      await apiService.reorderAIStyles(category, payload);
      dispatch(addNotification({ type: 'success', message: `Order saved for ${category}` }));
      setUnsavedCategories(prev => {
        const next = new Set(prev);
        next.delete(category);
        return next;
      });
      if (unsavedCategories.size <= 1) setHasUnsavedOrder(false);
    } catch (error: any) {
      dispatch(addNotification({ type: 'error', message: error?.message || 'Failed to save order' }));
    } finally {
      setSavingOrder(false);
    }
  };

  const handleDiscardOrder = (category: string, currentItems: OrderItem[]) => {
    initPendingOrder(category, currentItems);
    setUnsavedCategories(prev => {
      const next = new Set(prev);
      next.delete(category);
      return next;
    });
    if (unsavedCategories.size <= 1) setHasUnsavedOrder(false);
  };

  return {
    pendingOrder,
    hasUnsavedOrder,
    unsavedCategories,
    savingOrder,
    activeDragId,
    activeDragName,
    sensors,
    customCollisionDetection,
    getOrderedItems,
    handleDragStart,
    handleDragOver,
    handleDragCancel,
    handleDragEnd,
    handleSaveOrder,
    handleDiscardOrder,
  };
}
